import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, Inbox } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { RecordFilters, defaultFilters, toParams } from "./RecordFilters";
import { TotalsStrip } from "./TotalsStrip";
import { RecordRow } from "./RecordRow";
import { RecordSheet } from "./RecordSheet";
import { coMessage, fmt } from "./coUtils";

const PAGE = 25;

function ExportButton({ params, ids }) {
    const { t, lang } = useI18n();
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const r = await api.get("/co/export", { params: { ...params, lang }, responseType: "blob", timeout: 120000 });
            const name = (r.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "cancellations.xlsx";
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            toast.success(t("co_rec_exported"));
        } catch {
            toast.error(t("co_rec_export_failed"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button type="button" data-testid={ids.exportBtn} onClick={run} disabled={busy}
                className="inline-flex h-9 items-center gap-1.5 rounded-full bg-[color:var(--brand-brown)] px-4 text-xs font-bold text-white shadow-[var(--shadow-sm)] transition-[opacity,transform] duration-150 active:scale-[0.97] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span>{busy ? t("co_rec_exporting") : t("co_rec_export")}</span>
        </button>
    );
}

function Pager({ total, offset, onPage, ids, lang, t }) {
    const n = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v);
    const btn = "h-9 rounded-full border border-[color:var(--border-soft)] px-4 text-xs font-bold text-[color:var(--ink)] transition-[background-color] duration-150 hover:bg-[color:var(--bg-surface-2)] disabled:opacity-40";
    return (
        <div className="flex items-center justify-between gap-2 pt-2">
            <button type="button" data-testid={ids.prev} className={btn} disabled={offset === 0} onClick={() => onPage(Math.max(0, offset - PAGE))}>
                {t("co_rec_prev")}
            </button>
            <span data-testid={ids.pageInfo} className="text-[11px] font-semibold text-[color:var(--ink-muted)]">
                {fmt(t("co_rec_page"), { from: n(total ? offset + 1 : 0), to: n(Math.min(offset + PAGE, total)), total: n(total) })}
            </span>
            <button type="button" data-testid={ids.next} className={btn} disabled={offset + PAGE >= total} onClick={() => onPage(offset + PAGE)}>
                {t("co_rec_next")}
            </button>
        </div>
    );
}

/** «السجلات» (`kind = records`) and «يحتاج مراجعة» (`kind = review`, preset to pending +
 *  needs_follow_up). Same list, filters, totals strip, export and record panel. */
export function RecordsView({ scope, kind }) {
    const { t, lang } = useI18n();
    const ids = OPS.co.records(kind === "review" ? "co-review" : "co-records");
    const preset = useMemo(() => (kind === "review" ? { review_status: "open" } : {}), [kind]);
    const fallback = useMemo(() => defaultFilters(scope, preset), [scope, preset]);
    const [filters, setFilters] = useState(fallback);
    const [offset, setOffset] = useState(0);
    const [data, setData] = useState(null);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const params = useMemo(() => toParams(filters, fallback), [filters, fallback]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        const { date_from, date_to, show_duplicates, ...rest } = params; // eslint-disable-line no-unused-vars
        Promise.all([
            api.get("/co/records", { params: { ...params, limit: PAGE, offset } }),
            api.get("/co/reports/pos-match", { params: { ...rest, from: date_from, to: date_to } }),
        ])
            .then(([l, r]) => { if (alive) { setData(l.data); setTotals(r.data.total); } })
            .catch((e) => { if (alive) toast.error(coMessage(e, lang) || t("co_offline_error")); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [params, offset, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const onFilters = (f) => { setFilters(f); setOffset(0); };
    const items = data?.items || [];
    const many = (scope?.branches || []).length > 1;

    return (
        <div data-testid={ids.view} className="space-y-3">
            <RecordFilters scope={scope} value={filters} onChange={onFilters} ids={ids} lockReview={kind === "review"} preset={preset} />
            <TotalsStrip totals={totals} ids={ids} loading={loading} />
            <section className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <header className="flex items-center justify-between gap-2 pb-1">
                    <h3 className="text-sm font-bold text-[color:var(--ink)]">{t(kind === "review" ? "co_tab_review" : "co_tab_records")}</h3>
                    <ExportButton params={params} ids={ids} />
                </header>
                {loading && !data ? <InlineLoader /> : items.length ? (
                    <ul data-testid={ids.list} className={`divide-y divide-[color:var(--border-soft)] transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
                        {items.map((r) => <RecordRow key={r.id} rec={r} scope={scope} ids={ids} onOpen={setOpenId} showBranch={many} />)}
                    </ul>
                ) : (
                    <p data-testid={ids.empty} className="flex flex-col items-center gap-2 py-10 text-sm text-[color:var(--ink-muted)]">
                        <Inbox className="h-6 w-6 text-[color:var(--ink-faint)]" />{t("co_rec_empty")}
                    </p>
                )}
                {data?.total > PAGE ? <Pager total={data.total} offset={offset} onPage={setOffset} ids={ids} lang={lang} t={t} /> : null}
            </section>
            <RecordSheet recordId={openId} scope={scope} onClose={() => setOpenId(null)} onChanged={() => setReloadKey((k) => k + 1)} />
        </div>
    );
}
